/*
 * Define a form page to allow a user to enter the narrative text.
 */
import css from './pages.module.css'
import { narrativeLengthMessage, requiredMessage } from '../strings/messages'
import { multilineTextPage } from './multilineTextPage'
import * as yup from 'yup'

/**
 * Normalize various Unicode apostrophes and quotation marks to ASCII.
 * - Apostrophes → '
 * - Quotes → "
 * This runs before validation so your ASCII-only regex continues to work.
 * @param {string} [input]
 * @returns {string}
 */
const normalizeQuotes = input => {
  if (!input) return ''

  return (
    input
      // Apostrophes / single quotes variants → '
      .replace(
        /[\u2018\u2019\u201A\u201B\u2032\u2035\u275B\u275C\uFF07\u02BC\u00B4\u02BB\u0091\u0092]/g,
        "'"
      )
      // Double quotes variants → "
      .replace(
        /[\u201C\u201D\u201E\u201F\u2033\u2036\u275D\u275E\uFF02\u0093\u0094]/g,
        '"'
      )
      .replace(/[`]/g, "'")
  )
}

/**
 * Custom validator that:
 * 1) Normalizes quotes/apostrophes to ASCII using a Yup transform.
 * 2) Enforces required and max length against the normalized value.
 * 3) Applies the original ASCII-only regex.
 */
const noEmojiValidator = yup
  .string()
  .transform(val => normalizeQuotes(val))
  .required(requiredMessage)
  .max(10000, narrativeLengthMessage)
  .test('no-emojis', 'Special characters and emojis are not allowed.', value =>
    /^[\x09\x0A\x0D\x20-\x7F]*$/.test(value || '')
  )

const defaultHint =
  'This is your opportunity to provide additional details about your complaint. Our investigations are most successful when you provide as much information as possible about the allegation and those involved. The more you tell us, the better chance we have of determining whether an investigation can be pursued. Please provide details about the alleged wrongdoing including, if applicable, when it happened, where it happened, how it was committed and how you came to learn about the wrongdoing. If you have supporting documentation, you will have the opportunity to attach those files on the following page.'

/**
 * @typedef {Object} NarrativeProps
 * @property {string} pageId
 * @property {string} fieldName
 * @property {boolean} [required]
 * @property {number} [maxLength]
 * @property {boolean} [displayCharsRemaining]
 * @property {string} [lengthMessage]
 * @property {number} [rows]
 * @property {string} [label]
 * @property {string} [hint]
 * @property {string} [labelClassName]
 * @property {import('yup').AnyObjectSchema} [validation]
 */

/**
 * @param {Partial<NarrativeProps>} [propOverrides]
 */
export const narrativePage = (propOverrides = {}) => {
  const props = {
    pageId: 'narrative_page',
    fieldName: 'narrative',
    required: true,
    maxLength: 10000,
    displayCharsRemaining: true,
    lengthMessage: narrativeLengthMessage,
    rows: 8,
    label: 'Please describe the fraudulent action in your own words',
    hint: defaultHint,
    labelClassName: css.largeLabel,
    validation: yup.object().shape({
      narrative: noEmojiValidator
    }),
    ...propOverrides
  }

  return multilineTextPage({ ...props })
}
